import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

export const StripeAccount = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [stripeStatus, setStripeStatus] = useState<string>("not_started");
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchStripeStatus();
  }, []);

  const fetchStripeStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile, error } = await supabase
        .from('business_profiles')
        .select('stripe_account_id, stripe_details_submitted, stripe_charges_enabled, stripe_payouts_enabled, sepa_enabled')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) throw error;

      if (!profile) {
        const { data: newProfile, error: insertError } = await supabase
          .from('business_profiles')
          .insert({
            user_id: session.user.id,
            business_name: '',
            business_email: '',
            business_phone: '',
            business_address: '',
            business_tax_id: '',
            business_iban: '',
            business_swift: ''
          })
          .select('stripe_account_id, stripe_details_submitted, stripe_charges_enabled, stripe_payouts_enabled, sepa_enabled')
          .single();

        if (insertError) throw insertError;

        setStripeStatus('not_started');
        setStripeAccountId(null);
        return;
      }

      let status = 'not_started';
      if (profile.stripe_account_id) {
        if (profile.stripe_charges_enabled) {
          status = 'active';
        } else if (profile.stripe_details_submitted) {
          status = 'pending';
        } else {
          status = 'onboarding';
        }
      }

      setStripeStatus(status);
      setStripeAccountId(profile.stripe_account_id);
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
      toast.error(t("toasts.failed_load_stripe_account_status"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueSetup = async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const { data: continueProfile, error } = await supabase
        .from('business_profiles')
        .select('stripe_account_id, stripe_details_submitted, stripe_charges_enabled, stripe_payouts_enabled, sepa_enabled, business_name, business_email, business_phone, business_tax_id, business_iban, business_swift, country, street_address, postal_code, city, bank_name')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) throw error;

      if (!stripeAccountId || (continueProfile && !continueProfile.stripe_details_submitted)) {
        // Check if all required fields are filled
        const requiredFields = [
          'business_email', 'business_phone', 'business_tax_id',
          'business_iban', 'business_swift', 'country',
          'street_address', 'postal_code', 'city', 'bank_name'
        ];

        const isProfileComplete = continueProfile && requiredFields.every(field =>
          continueProfile[field] !== null &&
          continueProfile[field] !== undefined &&
          continueProfile[field] !== ''
        );

        if (isProfileComplete) {
          // If profile is complete, navigate directly to the third stage
          navigate('/setup-account?step=2');
        } else {
          // Otherwise, start from the beginning
          navigate('/setup-account');
        }
        return;
      }

      window.open('https://dashboard.stripe.com', '_blank');
    } catch (error) {
      console.error('Error continuing Stripe setup:', error);
      toast.error(t("toasts.failed_continue_stripe_setup"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    try {
      setIsLoading(true);
      const { data: urlData, error: urlError } = await supabase.functions.invoke(
        "connect-stripe-account",
        { body: {} }
      );

      if (urlError) throw urlError;
      if (urlData?.url) {
        window.open(urlData.url, '_blank');
      } else {
        throw new Error('No connection URL received');
      }
    } catch (error) {
      console.error('Error connecting Stripe account:', error);
      toast.error(t("toasts.failed_connect_stripe_account"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.functions.invoke("delete-stripe-account", {
        body: { accountId: stripeAccountId }
      });

      if (error) throw error;

      await supabase
        .from('business_profiles')
        .update({
          stripe_account_id: null,
          stripe_details_submitted: false,
          stripe_charges_enabled: false,
          stripe_payouts_enabled: false,
          sepa_enabled: false
        })
        .eq('stripe_account_id', stripeAccountId);

      toast.success(t("toasts.stripe_account_delete_success"));
      setStripeAccountId(null);
      setStripeStatus('not_started');
    } catch (error) {
      console.error('Error deleting Stripe account:', error);
      toast.error(t("toasts.stripe_account_delete_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      setIsLoading(true);
      window.open('https://dashboard.stripe.com', '_blank');
    } catch (error) {
      console.error('Error opening Stripe dashboard:', error);
      toast.error(t("toasts.failed_open_stripe_dashboard"));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="flex flex-col space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-sm text-[#34375B]">{t("account_settings.stripe_account_status", "Stripe Account Status")}: </h3>
            <span className={`capitalize ${stripeStatus === 'active' ? 'text-green-600' :
              stripeStatus === 'pending' ? 'text-amber-600' :
                stripeStatus === 'onboarding' ? 'text-amber-600' :
                  'text-gray-600'
              }`}>
              {t(`common.stripe_status.${stripeStatus}`)}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {(stripeStatus === 'onboarding' || stripeStatus === 'pending') && (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            )}
          </div>
        </div>

        {stripeStatus === 'active' && (
          <div className="text-left bg-white border border-[#8791F3] rounded-md p-4 text-sm text-[#34375B]">
            <p className="mb-2 text-sm font-medium text-[#34375B]/75">{t("account_settings.avoid_high_fees")}</p>
            <ol className="list-decimal list-inside space-y-1 text-[#34375B]/75">
              <li>{t("account_settings.sepa_step_1")}{" "}
                <a
                  href="https://dashboard.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-karla text-[#8791F3] hover:text-[#5CC2BB] underline"
                >
                  {t("account_settings.stripe_dashboard")}
                </a>
              </li>
              <li>{t("account_settings.sepa_step_4")}</li>
              <li>{t("account_settings.sepa_step_7")}</li>
            </ol>

          </div>
        )}

        {stripeStatus === 'pending' && (
          <div className="text-left bg-white border border-[#8791F3] rounded-md p-4 text-sm text-[#34375B]">
            <h4 className="font-semibold mb-4">{t("account_settings.sepa_setup_required")}</h4>
            <p className="mb-2 text-sm font-medium text-[#34375B]/75">{t("account_settings.sepa_setup_instructions")}</p>
            <ol className="list-decimal list-inside space-y-1 text-[#34375B]/75">
              <li>{t("account_settings.sepa_step_1")}{" "}
                <a
                  href="https://dashboard.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-karla text-[#8791F3] hover:text-[#5CC2BB] underline"
                >
                  {t("account_settings.stripe_dashboard")}
                </a>
              </li>
              <li>{t("account_settings.sepa_step_2")}</li>
              <li>{t("account_settings.sepa_step_3")}</li>
              <li>{t("account_settings.sepa_step_4")}</li>
              <li>{t("account_settings.sepa_step_5")}</li>
              <li>{t("account_settings.sepa_step_6")}</li>
            </ol>
            <p className="font-karla mb-2 pt-2 text-sm font-medium text-[#34375B]/75">{t("account_settings.accoun_will_be_activated")}</p>
            <p className="font-karla mb-2 pt-2 text-sm font-medium text-[#34375B]/55"><strong>{t("account_settings.note")}</strong> {t("account_settings.lower_fees")}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 sm:gap-4 justify-center">
          {stripeStatus === 'not_started' ? (
            <Button onClick={handleContinueSetup} className="w-full sm:w-auto bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200">
              {t("account_settings.setup_stripe_account", "Set up Stripe Account")}
            </Button>
          ) : stripeStatus === 'onboarding' ? (
            <>
              <Button onClick={handleContinueSetup} variant="secondary" className="w-full sm:w-auto text-[#F1F6F5] bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200">
                {t("account_settings.continue_stripe_setup", "Continue Stripe Setup")} <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
              {/* <Button onClick={handleConnectStripe} variant="default" className="w-full sm:w-auto bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200">
                {t("account_settings.connect_stripe_account", "Connect Stripe Account")} <ExternalLink className="ml-2 h-4 w-4" />
              </Button> */}
            </>
          ) : stripeStatus === 'pending' ? (
            <Button onClick={handleContinueSetup} className="w-full sm:w-auto text-[#F1F6F5] bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200">
              {t("account_settings.continue_stripe_setup", "Continue Stripe Setup")} <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          ) : stripeStatus === 'active' ? (
            <Button onClick={handleOpenDashboard} className="w-full sm:w-auto text-[#F1F6F5] bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200">
              {t("account_settings.go_to_stripe_dashboard", "Go to Stripe Dashboard")} <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          ) : null}

          {stripeAccountId && stripeStatus !== 'onboarding' && (
            <Button
              onClick={handleDeleteAccount}
              variant="destructive"
              className="w-full sm:w-auto bg-destructive/70 hover:bg-destructive hover:border-0 hover:text-desctructive hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
            >
              {t("account_settings.delete_stripe_account", "Delete Stripe Account")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
